import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const ThemeModeContext = createContext();

export const useThemeMode = () => useContext(ThemeModeContext);

const STORAGE_KEY = "themeMode";

const getSystemMode = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

// Brand colors are set per-project via REACT_APP_COLOR_* env vars (see the
// *_DARKMODE variants below), overriding the defaults in colors.module.css
// at runtime - re-applied whenever the theme mode changes.
const BRAND_COLOR_VARS = [
  {
    cssVar: "--color-primary",
    light: "REACT_APP_COLOR_PRIMARY",
    dark: "REACT_APP_COLOR_PRIMARY_DARKMODE",
  },
  {
    cssVar: "--color-primary-dark",
    light: "REACT_APP_COLOR_PRIMARY_DARK",
    dark: "REACT_APP_COLOR_PRIMARY_DARK_DARKMODE",
  },
  {
    cssVar: "--color-primary-light",
    light: "REACT_APP_COLOR_PRIMARY_LIGHT",
    dark: "REACT_APP_COLOR_PRIMARY_LIGHT_DARKMODE",
  },
];

const applyBrandColors = (mode) => {
  BRAND_COLOR_VARS.forEach(({ cssVar, light, dark }) => {
    const value =
      (mode === "dark" && process.env[dark]) || process.env[light];
    if (value) {
      document.documentElement.style.setProperty(cssVar, value);
    }
  });
};

export const ThemeModeProvider = ({ children }) => {
  const [explicitMode, setExplicitMode] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null
  );
  const [systemMode, setSystemMode] = useState(getSystemMode);

  const mode = explicitMode || systemMode;

  useEffect(() => {
    if (explicitMode) return undefined;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => setSystemMode(mql.matches ? "dark" : "light");
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, [explicitMode]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    applyBrandColors(mode);
  }, [mode]);

  const toggleMode = () => {
    const next = mode === "dark" ? "light" : "dark";
    setExplicitMode(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: process.env.REACT_APP_COLOR_PRIMARY || "#0F9D58" },
          background: {
            default: "var(--color-bg)",
            paper: "var(--color-bg-white)",
          },
        },
        typography: {
          fontFamily: '"Inter", "Segoe UI", sans-serif',
        },
      }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={{ mode, toggleMode }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
